import { type FC } from 'react';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import { getIcon } from '@/common/lib/getIcon';
import { type BingoTile as BingoTileInterface } from '@/common/types/bingo';
import { CurrencyImage } from '@/components/ui/CurrencyImage/CurrencyImage';

import css from './styles.module.scss';

const STATIC_IMG: Record<string, string> = {
  goldTicket: '/img/icons/ticket-gold.webp',
  silverTicket: '/img/icons/ticket-silver.webp',
  diamondTicket: '/img/icons/ticket-diamond.webp',
  starPoints: '/img/icons/star-large.png',
  boardStarPoints: '/img/icons/star-large.png',
  bingoBalls: '/img/bingo/bingo-icon.png',
};

const CURRENCY_MAP: Record<string, 'tgem' | 'dice' | 'star' | 'cash'> = {
  tgem: 'tgem',
  dice: 'dice',
  cash: 'cash',
};

function getTileIcon(rewardType: string): string | null {
  if (STATIC_IMG[rewardType]) return STATIC_IMG[rewardType];
  const icon = getIcon(rewardType);

  if (icon) return icon.webp ?? icon.png;

  return null;
}

interface BingoTileProps {
  tile: BingoTileInterface;
  isNewlyCompleted?: boolean;
  revealCounter?: number;
  isFlashing?: boolean;
  onTileClick?: (id: number) => void;
}

const BingoTile: FC<BingoTileProps> = ({
  tile,
  isNewlyCompleted,
  revealCounter = 0,
  isFlashing,
  onTileClick,
}) => {
  const { kind, rewardType, state, value } = tile;
  const isCompleted = state === 'completed';
  const isReward = state === 'reward';
  const isActive = isCompleted || isReward;
  const isEdgeTile = kind !== 'normal';

  const currencyKind = CURRENCY_MAP[rewardType];
  const imgSrc = !currencyKind ? getTileIcon(rewardType) : null;

  return (
    <motion.div
      onClick={() => onTileClick?.(tile.id)}
      className={cn(css.tile, {
        [css.completed]: isCompleted,
        [css.reward]: isReward,
        [css.newlyCompleted]: isNewlyCompleted,
        [css.edgeTile]: isEdgeTile,
        [css.edgeUnlocked]: isEdgeTile && isReward,
      })}
      key={revealCounter}
      animate={
        isNewlyCompleted
          ? { scale: [1, 1.38, 0.88, 1.12, 0.96, 1] }
          : { scale: 1 }
      }
      transition={
        isNewlyCompleted
          ? { duration: 0.5, ease: 'easeOut' }
          : { duration: 0.2 }
      }
    >
      {isReward && !isEdgeTile && <div className={css.neonGlow} />}
      {isReward && !isEdgeTile && <div className={css.neonBorder} />}
      {isEdgeTile && isReward && <div className={css.edgeGlow} />}

      <AnimatePresence>
        {isNewlyCompleted && (
          <motion.div
            key={revealCounter}
            className={css.ripple}
            initial={{ scale: 0, opacity: 0.9 }}
            animate={{ scale: 6, opacity: 0 }}
            exit={{}}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isFlashing && (
          <motion.div
            className={css.flashOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0, 1, 0, 1, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.0, ease: 'easeInOut' }}
          />
        )}
      </AnimatePresence>

      <div className={css.iconWrap}>
        {currencyKind ? (
          <CurrencyImage
            kind={currencyKind}
            size="36px"
            className={cn(isActive && css.iconActive)}
          />
        ) : imgSrc ? (
          <img
            src={imgSrc}
            alt={rewardType}
            className={cn(css.icon, isActive && css.iconActive)}
          />
        ) : null}
      </div>

      <div className={css.valueBadge}>
        <span className={css.valueText}>+{value}</span>
      </div>
    </motion.div>
  );
};

export { BingoTile };
